/**
 * Helper functions for the Action Registry.
 *
 * Contains title generation functions and compact renderers extracted from
 * registry.tsx to keep the registry file within the 300-line guideline.
 */

import {
  deriveEntityType,
  extractValue,
  parseAssociationType,
} from '@/components/proposals/shared/utils';
import { pluralizeEntityLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { stripEntityTags } from '@/lib/markdown/entityRendering';
import { cx } from '@/utils/cx';

import type {
  ActionPayload,
  AssociationAddedEntityPayload,
  AssociationRemovedEntityPayload,
  CalendarEventCreatePayload,
  ExternalActionPayload,
  GmailSendPayload,
  ObjectCreatedEntityPayload,
  ObjectDeletedEntityPayload,
  ObjectMergedEntityPayload,
  OnboardingActionPayload,
  PropertyUpdateEntityPayload,
  ProposedAction,
} from '../core/types';
import {
  isAssociationAddedPayload,
  isAssociationRemovedPayload,
  isCalendarEventCreatePayload,
  isExternalActionPayload,
  isGmailSendPayload,
  isObjectCreatedPayload,
  isObjectDeletedPayload,
  isObjectMergedPayload,
  isOnboardingActionPayload,
  isPropertyUpdatePayload,
} from '../core/types';
import { resolveEntityName } from '../utils/entityName';
import { resolvableEntityId } from '../utils/resolvableEntityId';
import { MaybeEntityBadge } from './MaybeEntityBadge';
import type { ActionRendererProps, SuccessStateConfig } from './types';

// ============================================================================
// Shared Helpers
// ============================================================================

/**
 * Resolve an entity_type_name to a lowercase display label.
 * Centralises the deriveEntityType → getEntityTypeLabel → toLowerCase chain
 * used across all title and approve-label helpers.
 *
 * @example entityLabel('ahoy_contact') // "contact"
 * @example entityLabel('ahoy_company') // "company"
 */
function entityLabel(entityTypeName: string): string {
  return getTypeLabelByName(deriveEntityType(entityTypeName)).toLowerCase();
}

/**
 * Append ": {entity name}" to a base title when object_label is present.
 * Used across title functions for property updates, stage associations,
 * object create, and object delete actions.
 */
function appendEntityName(baseTitle: string, action: ProposedAction): string {
  const objectLabel = action.enriched_data?.object_label;
  return objectLabel ? `${baseTitle}: ${objectLabel}` : baseTitle;
}

/**
 * Resolve the display label for an association's source entity, mirroring
 * LinkActionCard.tsx's sourceEntityType resolution: prefer
 * `action.enriched_data.entity_type_name`, falling back to the payload's own
 * `entity_type_name`. A converted task riding a deal edge (e.g.
 * ahoy_deal_to_contact with a task source) must NOT get "deal" copy — only
 * use the declared association source label when it matches the actual
 * source entity type.
 */
function resolveAssociationSourceLabel(
  declaredSource: string,
  payloadEntityTypeName: string,
  action: ProposedAction
): string {
  const actualType =
    action.enriched_data?.entity_type_name || payloadEntityTypeName;
  if (!actualType || actualType === `ahoy_${declaredSource}`) {
    return entityLabel(declaredSource);
  }
  return entityLabel(actualType);
}

// ============================================================================
// Title Generation Helpers
// ============================================================================

/**
 * Get title for Gmail send action
 */
export function getGmailTitle(payload: ActionPayload): string {
  if (isGmailSendPayload(payload)) {
    const gmailPayload = payload as GmailSendPayload;
    return gmailPayload.action_params.subject || 'No subject';
  }
  return 'Email';
}

/**
 * Get title for Calendar event action
 */
export function getCalendarTitle(payload: ActionPayload): string {
  if (isCalendarEventCreatePayload(payload)) {
    const calendarPayload = payload as CalendarEventCreatePayload;
    return calendarPayload.action_params.title || 'No title';
  }
  return 'Calendar event';
}

/**
 * Get title for External action
 */
export function getExternalActionTitle(payload: ActionPayload): string {
  if (isExternalActionPayload(payload)) {
    const externalPayload = payload as ExternalActionPayload;
    return (
      stripEntityTags(externalPayload.action_params.title ?? '').trim() ||
      'External action'
    );
  }
  return 'External action';
}

/**
 * Get title for Onboarding action
 */
export function getOnboardingActionTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  // Use action parameter to satisfy registry type signature
  void action;
  if (isOnboardingActionPayload(payload)) {
    return (
      (payload as OnboardingActionPayload).action_params.title ||
      'Onboarding action'
    );
  }
  return 'Onboarding action';
}

/**
 * Check whether an association type name refers to a timeline association.
 * Parses the `ahoy_<source>_to_<target>` format and returns true when
 * either the source or target ends with `_timeline`. This is stricter than
 * a raw `.includes('_timeline')` (which would false-positive on entity
 * types that merely *contain* the substring) while still catching both
 * source-side and target-side timeline associations.
 */
function isTimelineAssociation(associationTypeName: string): boolean {
  const { source, target } = parseAssociationType(associationTypeName);
  return source.endsWith('_timeline') || target.endsWith('_timeline');
}

/** Pattern matching stage/lifecycle property keys */
const STAGE_PROP_PATTERN = /stage|lifecycle/i;

/**
 * Get title for Property update action.
 *
 * For stage transitions, returns a specific title like "Update deal stage"
 * or "Update lifecycle stage" instead of the generic "Update deal".
 */
export function getPropertyUpdateTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isPropertyUpdatePayload(payload)) {
    const propPayload = payload as PropertyUpdateEntityPayload;
    const label = entityLabel(propPayload.entity_type_name);
    const propKeys = Object.keys(propPayload.payload.properties);

    let baseTitle: string;
    // Detect stage-only updates for specific titles
    if (propKeys.length === 1 && STAGE_PROP_PATTERN.test(propKeys[0])) {
      if (propPayload.entity_type_name === 'ahoy_contact') {
        baseTitle = 'Update lifecycle stage';
      } else {
        baseTitle = `Update ${label} stage`;
      }
    } else {
      baseTitle = `Update ${label}`;
    }

    return appendEntityName(baseTitle, action);
  }
  return 'Update properties';
}

/**
 * Get title for Object created action
 */
export function getObjectCreatedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isObjectCreatedPayload(payload)) {
    const createPayload = payload as ObjectCreatedEntityPayload;

    // Property type creation: "Create new Contact property" (target entity
    // type instead of "property type" which is the entity_type_name).
    if (createPayload.entity_type_name === 'ahoy_property_type') {
      const targetRaw = extractValue(
        createPayload.payload.properties.ahoy_entity_type_name
      );
      const targetLabel =
        typeof targetRaw === 'string'
          ? getTypeLabelByName(targetRaw).toLowerCase()
          : 'custom';
      const baseTitle = `Create new ${targetLabel} property`;
      return appendEntityName(baseTitle, action);
    }

    // Entity type creation: "Create new custom entity: Tickets"
    if (createPayload.entity_type_name === 'ahoy_entity_type') {
      const baseTitle = 'Create new custom object type';
      return appendEntityName(baseTitle, action);
    }

    const label = entityLabel(createPayload.entity_type_name);
    // Risks use "Add" instead of "Create"
    const verb =
      createPayload.entity_type_name === 'ahoy_risk' ? 'Add' : 'Create';
    const baseTitle = `${verb} new ${label}`;
    // Prefer the entity's real name from the payload (e.g. a custom object's
    // ahoy_display_name) over enriched_data.object_label, which for custom
    // entities is just the generic type label ("New Org Bug"). Notes are the
    // exception: a title-less note's object_label is its body preview, which
    // the card already renders below the header — suffixing it here would
    // duplicate the body, so the header stays a bare "Create new note".
    const rawName =
      resolveEntityName(
        createPayload.payload.properties,
        createPayload.entity_type_name
      ) ??
      (createPayload.entity_type_name === 'ahoy_note'
        ? undefined
        : action.enriched_data?.object_label);
    // The header is plain text (no markdown rendering), so strip any
    // `<entity>` markup the model put in the name — mirrors
    // getExternalActionTitle above.
    const name = rawName ? stripEntityTags(rawName).trim() : undefined;
    return name ? `${baseTitle}: ${name}` : baseTitle;
  }
  return 'Add new item';
}

/**
 * Get title for Object deleted action
 */
export function getObjectDeletedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isObjectDeletedPayload(payload)) {
    const deletePayload = payload as ObjectDeletedEntityPayload;
    const label = entityLabel(deletePayload.entity_type_name);
    const baseTitle = `Delete ${label}`;
    return appendEntityName(baseTitle, action);
  }
  return 'Delete item';
}

/**
 * Get title for Association added action.
 * e.g. "Associate contact"
 */
export function getAssociationAddedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationAddedPayload(payload)) {
    const assocPayload = payload as AssociationAddedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const { source } = parseAssociationType(
      assocPayload.payload.association_type_name
    );
    const label = resolveAssociationSourceLabel(
      source,
      assocPayload.entity_type_name,
      action
    );
    return `Associate ${label}`;
  }
  return 'Associate item';
}

/**
 * Get title for Association removed action.
 * e.g. "Remove contact from company"
 */
export function getAssociationRemovedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationRemovedPayload(payload)) {
    const assocPayload = payload as AssociationRemovedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const { source, target } = parseAssociationType(
      assocPayload.payload.association_type_name
    );
    const sourceLabel = resolveAssociationSourceLabel(
      source,
      assocPayload.entity_type_name,
      action
    );
    return `Remove ${sourceLabel} from ${entityLabel(target)}`;
  }
  return 'Remove association';
}

/**
 * Get title for stage association added action.
 *
 * Uses the entity type from the payload to generate dynamic titles
 * (e.g. "Update contact stage", "Update deal stage") that work for
 * custom entity types without hardcoded mappings.
 */
export function getStageAssociationAddedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationAddedPayload(payload)) {
    const assocPayload = payload as AssociationAddedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const objectLabel = action.enriched_data?.object_label;
    const stageLabel = action.enriched_data?.target_object?.label;

    // Notes and tasks read "Mark [record] as [stage]"; the no-stage fallback is a plain "Update stage".
    if (
      assocPayload.entity_type_name === 'ahoy_note' ||
      assocPayload.entity_type_name === 'ahoy_task'
    ) {
      if (objectLabel && stageLabel) {
        return `Mark ${objectLabel} as ${stageLabel}`;
      }
      return appendEntityName('Update stage', action);
    }
    // Other entities: "Move [entity] to [stage]".
    if (objectLabel && stageLabel) {
      return `Move ${objectLabel} to ${stageLabel}`;
    }
    // Fallback when enriched data is incomplete.
    const { target } = parseAssociationType(
      assocPayload.payload.association_type_name
    );
    const label = target.replace(/_/g, ' ');
    return appendEntityName(`Update ${label}`, action);
  }
  return 'Update stage';
}

/**
 * Get title for stage association removed action.
 *
 * Uses the entity type from the payload to generate dynamic titles
 * (e.g. "Remove contact stage", "Remove deal stage") that work for
 * custom entity types without hardcoded mappings.
 */
export function getStageAssociationRemovedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationRemovedPayload(payload)) {
    const assocPayload = payload as AssociationRemovedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const objectLabel = action.enriched_data?.object_label;
    const stageLabel = action.enriched_data?.target_object?.label;

    // Notes and tasks read "Remove [record] from [stage]"; the no-stage fallback is a plain "Remove stage".
    if (
      assocPayload.entity_type_name === 'ahoy_note' ||
      assocPayload.entity_type_name === 'ahoy_task'
    ) {
      if (objectLabel && stageLabel) {
        return `Remove ${objectLabel} from ${stageLabel}`;
      }
      return appendEntityName('Remove stage', action);
    }
    // Other entities: "Remove [entity] from [stage]".
    if (objectLabel && stageLabel) {
      return `Remove ${objectLabel} from ${stageLabel}`;
    }
    // Fallback when enriched data is incomplete.
    const { target } = parseAssociationType(
      assocPayload.payload.association_type_name
    );
    const label = target.replace(/_/g, ' ');
    return appendEntityName(`Remove ${label}`, action);
  }
  return 'Remove stage';
}

/**
 * Get title for Object merged action
 */
export function getObjectMergedTitle(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isObjectMergedPayload(payload)) {
    const mergePayload = payload as ObjectMergedEntityPayload;
    // Deleted source is enriched as source_object; the survivor is the
    // top-level object_label.
    const sourceLabel = action.enriched_data?.source_object?.label;
    const targetLabel = action.enriched_data?.object_label;
    if (sourceLabel && targetLabel) {
      return `Merge ${sourceLabel} into ${targetLabel}`;
    }
    return `Merge ${entityLabel(mergePayload.entity_type_name)}`;
  }
  return 'Merge items';
}

// ============================================================================
// Approve Button Label Helpers
// ============================================================================

/** Get approve label for property update: "Update {entity type}" */
export function getPropertyUpdateApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  void action;
  if (isPropertyUpdatePayload(payload)) {
    const propPayload = payload as PropertyUpdateEntityPayload;
    return `Update ${entityLabel(propPayload.entity_type_name)}`;
  }
  return 'Update item';
}

/** Get approve label for object created: "Create {entity type}" or "Add risk" */
export function getObjectCreatedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  void action;
  if (isObjectCreatedPayload(payload)) {
    const createPayload = payload as ObjectCreatedEntityPayload;
    if (createPayload.entity_type_name === 'ahoy_property_type') {
      return 'Create property';
    }
    if (createPayload.entity_type_name === 'ahoy_entity_type') {
      return 'Create object type';
    }
    const label = entityLabel(createPayload.entity_type_name);
    if (createPayload.entity_type_name === 'ahoy_risk') {
      return `Add ${label}`;
    }
    return `Create ${label}`;
  }
  return 'Create item';
}

/** Get approve label for object deleted: "Delete {entity type}" */
export function getObjectDeletedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  void action;
  if (isObjectDeletedPayload(payload)) {
    const deletePayload = payload as ObjectDeletedEntityPayload;
    return `Delete ${entityLabel(deletePayload.entity_type_name)}`;
  }
  return 'Delete item';
}

/** Get approve label for association added: "Associate {source type}" */
export function getAssociationAddedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationAddedPayload(payload)) {
    const assocPayload = payload as AssociationAddedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const { source } = parseAssociationType(
      assocPayload.payload.association_type_name
    );
    const label = resolveAssociationSourceLabel(
      source,
      assocPayload.entity_type_name,
      action
    );
    return `Associate ${label}`;
  }
  return 'Associate item';
}

/** Get approve label for association removed: "Remove {source type} association" */
export function getAssociationRemovedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationRemovedPayload(payload)) {
    const assocPayload = payload as AssociationRemovedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const { source } = parseAssociationType(
      assocPayload.payload.association_type_name
    );
    const label = resolveAssociationSourceLabel(
      source,
      assocPayload.entity_type_name,
      action
    );
    return `Remove ${label} association`;
  }
  return 'Remove association';
}

/** Get approve label for stage association added: "Move to {stage}" */
export function getStageAssociationAddedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationAddedPayload(payload)) {
    const assocPayload = payload as AssociationAddedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const stageLabel = action.enriched_data?.target_object?.label;
    if (
      assocPayload.entity_type_name === 'ahoy_note' ||
      assocPayload.entity_type_name === 'ahoy_task'
    ) {
      return stageLabel ? `Mark as ${stageLabel}` : 'Update stage';
    }
    return stageLabel ? `Move to ${stageLabel}` : 'Update stage';
  }
  return 'Update stage';
}

/** Get approve label for stage association removed: "Remove from {stage}" */
export function getStageAssociationRemovedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  if (isAssociationRemovedPayload(payload)) {
    const assocPayload = payload as AssociationRemovedEntityPayload;
    if (isTimelineAssociation(assocPayload.payload.association_type_name)) {
      return 'Invalid association';
    }
    const stageLabel = action.enriched_data?.target_object?.label;
    if (assocPayload.entity_type_name === 'ahoy_note') {
      return stageLabel ? `Remove from ${stageLabel}` : 'Remove stage';
    }
    return stageLabel ? `Remove from ${stageLabel}` : 'Remove stage';
  }
  return 'Remove stage';
}

/** Get approve label for object merged: "Merge {entity type plural}" */
export function getObjectMergedApproveLabel(
  payload: ActionPayload,
  action: ProposedAction
): string {
  void action;
  if (isObjectMergedPayload(payload)) {
    const mergePayload = payload as ObjectMergedEntityPayload;
    return `Merge ${pluralizeEntityLabel(entityLabel(mergePayload.entity_type_name))}`;
  }
  return 'Merge items';
}

// ============================================================================
// Compact Mode Renderers
// ============================================================================

// ============================================================================
// Hidden Entity Helpers
// ============================================================================

/**
 * Get title for an action targeting a hidden entity. The entity exists in
 * the CRM but is not user-visible; approving executes the underlying action
 * (a stage change or property update) — it does NOT unhide anything, so the
 * copy says "update", never "add to CRM".
 */
export function getHiddenEntityAddTitle(
  _payload: ActionPayload,
  action: ProposedAction
): string {
  const label = action.enriched_data?.object_label;
  return label ? `Update ${label}` : 'Update item';
}

/**
 * Get approve label for an action targeting a hidden entity. Mirrors
 * getPropertyUpdateApproveLabel's phrasing.
 */
export function getHiddenEntityAddApproveLabel(
  _payload: ActionPayload,
  action: ProposedAction
): string {
  const type = action.enriched_data?.entity_type_name;
  return type ? `Update ${entityLabel(type)}` : 'Update item';
}

// ============================================================================
// Success State Helpers
// ============================================================================

/**
 * Shared success state for any action that produces the org's deal pipeline
 * (create_pipeline and onboarding_action:customize_pipeline).
 * Centralised so the title/description/secondary-button stay aligned across
 * both entry points.
 */
export function getPipelineSuccessState(orgId: string): SuccessStateConfig {
  return {
    title: 'Your pipeline is now live.',
    description:
      "Your deal pipeline is ready. As you add deals, we'll track their progress and let you know when they need attention.",
    secondaryButton: {
      label: 'View deal pipeline',
      getPath: () => `/${orgId}/crm/deals`,
    },
  };
}

// ============================================================================
// Compact Renderers
// ============================================================================

/**
 * Shared compact renderer for actions that describe "<verb> <entity>" — e.g.
 * "Update contact: Liam O'Brien", "Update lifecycle stage: Raj Patel",
 * "Create new deal: TechStart Enterprise", "Delete contact: Old Record".
 *
 * Renders the verb as secondary text followed by the entity as an EntityBadge
 * (avatar + name pill, clickable to open the slideout) so the table row has
 * the same visual treatment as the `renderAssociationCompact` rows — no more
 * plain-text entity names mixed in with badge rows.
 *
 * Callers pass the base verb (without trailing colon) plus the payload-
 * specific entity info. When the entity info isn't available we fall back
 * to rendering just the base verb.
 */
function renderEntityActionCompact({
  baseVerb,
  action,
  entityTypeName,
  entityId,
  orgId,
  className,
  displayNameOverride,
  preview,
}: {
  baseVerb: string;
  action: ProposedAction;
  /** Entity type name from the payload (e.g. 'ahoy_contact') */
  entityTypeName: string;
  /** Payload's entity_id — resolved to projected/live data by the store hook */
  entityId: string;
  orgId: number | undefined;
  className?: string;
  /** Real name resolved from the payload; wins over enriched_data.object_label. */
  displayNameOverride?: string;
  /** CRM preview overlay handle, threaded down from ActionRendererProps. */
  preview: ActionRendererProps['preview'];
}) {
  const enrichedData = action.enriched_data;
  const objectLabel = displayNameOverride ?? enrichedData?.object_label;
  const resolvedType = enrichedData?.entity_type_name ?? entityTypeName;
  const crmEntityId = resolvableEntityId(entityId, enrichedData, preview);

  if (!objectLabel || !resolvedType) {
    // Strip the trailing colon on the fallback — matches the string-form
    // `appendEntityName` which omits ": <name>" when no name exists.
    const verbWithoutColon = baseVerb.replace(/:\s*$/, '');
    return (
      <span className={cx('text-sm text-fg-primary truncate', className)}>
        {verbWithoutColon}
      </span>
    );
  }

  // Non-CRM entities render as plain text — no badge, no broken click target.
  // Keep the "<verb>: <name>" colon (a name IS present here) so the row matches
  // the string-form title from getObjectCreatedTitle/appendEntityName — e.g.
  // "Create new bug: New Org Bug" rather than "Create new bug New Org Bug".
  if (!crmEntityId) {
    return (
      <span className={cx('text-sm text-fg-primary truncate', className)}>
        {baseVerb} {objectLabel}
      </span>
    );
  }

  return (
    <div className={cx('flex items-center gap-1.5 text-sm', className)}>
      <span className='text-fg-secondary shrink-0'>{baseVerb}</span>
      <MaybeEntityBadge
        entityId={crmEntityId}
        orgId={orgId}
        entityType={resolvedType}
        displayName={objectLabel}
      />
    </div>
  );
}

/**
 * Compact renderer for property-update actions. Shows the verb ("Update
 * contact" / "Update lifecycle stage" / etc.) followed by an EntityBadge for
 * the entity instead of concatenating the name into the title string.
 */
export function renderPropertyUpdateCompact(props: ActionRendererProps) {
  const { action, orgId, className, preview } = props;
  const { payload } = action;
  if (!isPropertyUpdatePayload(payload)) {
    return (
      <span className={cx('text-sm text-fg-primary truncate', className)}>
        Update properties
      </span>
    );
  }
  const propPayload = payload as PropertyUpdateEntityPayload;
  const label = entityLabel(propPayload.entity_type_name);
  const propKeys = Object.keys(propPayload.payload.properties);

  // Mirror getPropertyUpdateTitle's verb derivation so the compact row matches
  // the header's phrasing — except we stop before the colon + name.
  let baseVerb: string;
  if (propKeys.length === 1 && STAGE_PROP_PATTERN.test(propKeys[0])) {
    baseVerb =
      propPayload.entity_type_name === 'ahoy_contact'
        ? 'Update lifecycle stage:'
        : `Update ${label} stage:`;
  } else {
    baseVerb = `Update ${label}:`;
  }

  return renderEntityActionCompact({
    baseVerb,
    action,
    entityTypeName: propPayload.entity_type_name,
    entityId: propPayload.entity_id,
    orgId,
    className,
    preview,
  });
}

/**
 * Compact renderer for stage-association add/remove actions.
 *
 * Shows both the source entity and target stage as inline badges:
 * - Add:    "Move [Entity Badge] to [Stage Badge]"
 * - Remove: "Remove [Entity Badge] from [Stage Badge]"
 * - Notes:  "Mark [Note Badge] as [Stage Badge]" / "Remove [Note Badge] from [Stage Badge]"
 */
export function renderStageAssociationCompact(props: ActionRendererProps) {
  const { action, orgId, className, preview } = props;
  const payload = action.payload as
    | AssociationAddedEntityPayload
    | AssociationRemovedEntityPayload;
  const enrichedData = action.enriched_data;
  const isRemoval = isAssociationRemovedPayload(action.payload);
  // `*_timeline` identifiers are not valid association types — surface the
  // same invalid-association fallback used elsewhere (e.g. LinkActionCard)
  // instead of rendering a broken "Update timeline: …" row. Uses
  // `.includes()` to also catch source-side timeline association types.
  if (isTimelineAssociation(payload.payload.association_type_name)) {
    return (
      <span className={cx('text-sm text-fg-secondary truncate', className)}>
        Invalid association type
      </span>
    );
  }

  // Source entity info (the entity being moved/marked)
  const sourceLabel =
    enrichedData?.object_label || entityLabel(payload.entity_type_name);
  const sourceType = enrichedData?.entity_type_name || payload.entity_type_name;
  const sourceEntityId = resolvableEntityId(
    payload.entity_id,
    enrichedData,
    preview
  );

  // Target stage info (the stage being moved to)
  const targetLabel = enrichedData?.target_object?.label || 'stage';
  const targetType =
    enrichedData?.target_object?.entity_type_name ||
    `ahoy_${parseAssociationType(payload.payload.association_type_name).target}`;
  const targetEntityId = resolvableEntityId(
    payload.payload.target_entity_id,
    enrichedData?.target_object,
    preview
  );

  // Notes and tasks read "Mark [record] as [stage]" / "Remove [record] from [stage]".
  // Other entities use movement language: "Move [entity] to [stage]".
  const isNoteLike =
    payload.entity_type_name === 'ahoy_note' ||
    payload.entity_type_name === 'ahoy_task';
  let verb: string;
  let connector: string;
  if (isNoteLike) {
    verb = isRemoval ? 'Remove' : 'Mark';
    connector = isRemoval ? 'from' : 'as';
  } else {
    verb = isRemoval ? 'Remove' : 'Move';
    connector = isRemoval ? 'from' : 'to';
  }

  return (
    <div className={cx('flex items-center gap-1.5 text-sm', className)}>
      <span className='text-fg-secondary shrink-0'>{verb}</span>
      <MaybeEntityBadge
        entityId={sourceEntityId}
        orgId={orgId}
        entityType={sourceType}
        displayName={sourceLabel}
      />
      <span className='text-fg-secondary shrink-0'>{connector}</span>
      <MaybeEntityBadge
        entityId={targetEntityId}
        orgId={orgId}
        entityType={targetType}
        displayName={targetLabel}
      />
    </div>
  );
}

/**
 * Compact renderer for object-create actions. "Create new <type>: <entity>"
 * with the entity as an EntityBadge.
 */
export function renderObjectCreatedCompact(props: ActionRendererProps) {
  const { action, orgId, className, preview } = props;
  const { payload } = action;
  if (!isObjectCreatedPayload(payload)) {
    return (
      <span className={cx('text-sm text-fg-primary truncate', className)}>
        Add new item
      </span>
    );
  }
  const createPayload = payload as ObjectCreatedEntityPayload;
  const label = entityLabel(createPayload.entity_type_name);
  const verb =
    createPayload.entity_type_name === 'ahoy_risk' ? 'Add' : 'Create';
  const baseVerb = `${verb} new ${label}:`;

  return renderEntityActionCompact({
    baseVerb,
    action,
    entityTypeName: createPayload.entity_type_name,
    entityId: createPayload.entity_id,
    orgId,
    className,
    // Prefer the real name from the payload (custom objects' ahoy_display_name)
    // over the generic object_label.
    displayNameOverride: resolveEntityName(
      createPayload.payload.properties,
      createPayload.entity_type_name
    ),
    preview,
  });
}

/**
 * Compact renderer for object-delete actions. "Delete <type>: <entity>" with
 * the entity as an EntityBadge.
 */
export function renderObjectDeletedCompact(props: ActionRendererProps) {
  const { action, orgId, className, preview } = props;
  const { payload } = action;
  if (!isObjectDeletedPayload(payload)) {
    return (
      <span className={cx('text-sm text-fg-primary truncate', className)}>
        Delete item
      </span>
    );
  }
  const deletePayload = payload as ObjectDeletedEntityPayload;
  const baseVerb = `Delete ${entityLabel(deletePayload.entity_type_name)}:`;

  return renderEntityActionCompact({
    baseVerb,
    action,
    entityTypeName: deletePayload.entity_type_name,
    entityId: deletePayload.entity_id,
    orgId,
    className,
    preview,
  });
}

/**
 * Compact renderer for association actions (added/removed)
 * Uses EntityBadge components for entity representation
 */
export function renderAssociationCompact(props: ActionRendererProps) {
  const { action, className = '', orgId, preview } = props;
  const payload = action.payload as
    | AssociationAddedEntityPayload
    | AssociationRemovedEntityPayload;
  const enrichedData = action.enriched_data;
  const isRemoving = isAssociationRemovedPayload(action.payload);

  if (isTimelineAssociation(payload.payload.association_type_name)) {
    return (
      <div className={cx('flex items-center gap-1.5 text-sm', className)}>
        <span className='text-fg-secondary'>Invalid association type</span>
      </div>
    );
  }

  const { source, target } = parseAssociationType(
    payload.payload.association_type_name
  );

  const sourceLabel = enrichedData?.object_label || getTypeLabelByName(source);
  const targetLabel =
    enrichedData?.target_object?.label || getTypeLabelByName(target);

  const sourceType = enrichedData?.entity_type_name || `ahoy_${source}`;
  const targetType =
    enrichedData?.target_object?.entity_type_name || `ahoy_${target}`;
  const sourceEntityId = resolvableEntityId(
    payload.entity_id,
    enrichedData,
    preview
  );
  const targetEntityId = resolvableEntityId(
    payload.payload.target_entity_id,
    enrichedData?.target_object,
    preview
  );

  return (
    <div className={cx('flex items-center gap-1.5 text-sm', className)}>
      <span className='text-fg-secondary shrink-0'>
        {isRemoving ? 'Remove' : 'Link'}
      </span>
      <MaybeEntityBadge
        entityId={sourceEntityId}
        orgId={orgId}
        entityType={sourceType}
        displayName={sourceLabel}
      />
      <span className='text-fg-secondary shrink-0'>
        {isRemoving ? 'from' : 'to'}
      </span>
      <MaybeEntityBadge
        entityId={targetEntityId}
        orgId={orgId}
        entityType={targetType}
        displayName={targetLabel}
      />
    </div>
  );
}
